import React, { ChangeEvent } from 'react';
import { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { ErrorMessageComponent } from '../../../Error/ErrorMessageComponent';

// styles (global: also provides .ToggleInline, used by other forms)
import './StageForm.Styles.css';

// component functions
import * as ComponentFunctions from './StageForm.Functions';
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import { NewContractStage } from '../../../../models/Entities/Contracts/NewContractStage';
import CycleSelectorComponent from '../../../../commons/component/Inputs/CyclesSelector/CycleSelectorComponent';
import { StageCycle } from '../../../../models/Entities/Contracts/StageCycle';

////i18Next
import { useTranslation } from 'react-i18next';
import {
    Button,
    Divider,
    Field,
    Input,
    InputOnChangeData,
    InputProps,
    makeStyles,
    MessageBarBody,
    MessageBarTitle,
    shorthands,
    Switch,
    SwitchOnChangeData,
    SwitchProps,
    Textarea,
    TextareaOnChangeData,
    TextareaProps,
    tokens
} from '@fluentui/react-components';
import { InfoFilled, Settings24Regular } from '@fluentui/react-icons';
import ProtocolVisitSelectorComponent from '../../../../commons/component/Inputs/ProtocolVisitsSelector/ProtocolVisitSelector.Component';
import { ProtocolVisit } from '../../../../models/Entities/Protocols/ProtocolVisit';
import ContractStageTypesSelectorComponent from '../../../../commons/component/Inputs/ContractStageTypesSelector/ContractStageTypes.Selector.Component';
import StageTypeListComponent from '../StageTypeList/StageTypeList.Component';
import { ContractStageType } from '../../../../models/Entities/Contracts/ContractStageType';
import ContractBranchSelectorComponent from '../../../../commons/component/Inputs/ContractBranchSelector/ContractBranchSelector.Component';
import { ContractBranch } from '../../../../models/Entities/Contracts/ContractBranch';
import { GuideDrawer } from '../../../../theme/components/GuideDrawer';
import { GuideField } from '../../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';

const useStageFormStyles = makeStyles({
    group: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalL,
    },
    typeRow: {
        display: 'flex',
        alignItems: 'flex-end',
        columnGap: tokens.spacingHorizontalS,
    },
    typeSelector: {
        flexGrow: 1,
        minWidth: 0,
    },
    typeSettings: {
        flexShrink: 0,
    },
    switches: {
        display: 'grid',
        rowGap: tokens.spacingVerticalM,
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
        ...shorthands.border(tokens.strokeWidthThin, 'solid', 'var(--ichtys-border)'),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
    },
    errorList: {
        ...shorthands.margin(0),
    },
});

interface FormValidation {
    success: boolean;
    errors: string[];
}

export const ContractStageFormComponent = (props: ComponentFunctions.Props) => {
    const styles = useStageFormStyles();
    const [isStageTypeDrawerOpen, setIsStageTypeDrawerOpen] = useState(false);

    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [FormValidationStatus, setFormValidationStatus] = useState<
        FormValidation | undefined
    >();

    const [stageName, setStageName] = useState<string>();
    const [comments, setComments] = useState<string>();
    const [isOptional, setIsOptional] = useState<boolean>(false);
    const [isOnSiteMandatory, setIsOnSiteMandatory] = useState<boolean>(false);
    const [cycleId, setCycleId] = useState<number | undefined | null>();
    const [failSelectionEnabled, setFailSelectionEnabled] = useState<boolean>(false);
    const [stageOrder, setStageOrder] = useState<number>();
    const [emrProtocolVisitIdRelated, setEmrProtocolVisitIdRelated] = useState<number>();
    const [markAsSettleable, setMarkAsSettleable] = useState<boolean>(true);
    const [markAsPayable, setMarkAsPayable] = useState<boolean>(true);
    const [contractStageTypeId, setContractStageTypeId] = useState<number | undefined>();
    const [branchId, setBranchId] = useState<number | undefined>();

    const stageCountAtSubmit = useRef<number>(-1);

    //useTranslation()
    const [t] = useTranslation();

    //load necesary data
    useEffect(() => {
        if (!props.Contract && props.ContractId) props.GetContractById(props.ContractId);
        if (!props.Stage && props.ContractStageId) props.GetContractStageById(props.ContractStageId);
    }, []);

    // save control
    useEffect(() => {
        if (submittedForm && FormValidationStatus?.success) {
            const addSuccess = !props.ContractStageId && stageCountAtSubmit.current >= 0 && props.TotalStages > stageCountAtSubmit.current;
            if (props.isSavedSuccessfully || addSuccess) {
                ResetForm();
                props.dismissPanel();
            }
        }
    }, [submittedForm, props.isSavedSuccessfully, props.TotalStages]);


    useEffect(() => {
        if (props.Stage?.id) {
            setStageName(props.Stage.stageName);
            setComments(props.Stage.comments);
            setIsOptional(props.Stage.isOptional);
            setIsOnSiteMandatory(props.Stage.isOnSiteMandatory);
            setCycleId(props.Stage.cycleId);
            setFailSelectionEnabled(props.Stage.failSelectionEnabled);
            setStageOrder(props.Stage.stageOrder ?? props.TotalStages);
            setEmrProtocolVisitIdRelated(props.Stage.emrProtocolVisitIdRelated);
            setMarkAsPayable(props.Stage.markAsPayable);
            setMarkAsSettleable(props.Stage.markAsSettleable);
            setContractStageTypeId(props.Stage.contractStageType?.id); // <-- sincroniza el tipo seleccionado
            setBranchId(props.Stage.branchId ?? undefined);
        } else {
            setStageOrder(props.TotalStages);
            setContractStageTypeId(undefined); // <-- limpia si es nuevo
            setBranchId(undefined);
        }
    }, [props.ContractStageId, props.Stage?.id]);

    const ResetForm = () => {
        stageCountAtSubmit.current = -1;
        setSubmittedForm(false);
        setStageName('');
        setComments('');
        setIsOptional(false);
        setIsOnSiteMandatory(false);
        setCycleId(undefined);
        setFailSelectionEnabled(false);
        setStageOrder(props.TotalStages);
        setEmrProtocolVisitIdRelated(undefined);
        setMarkAsPayable(true);
        setMarkAsSettleable(true);
        setBranchId(undefined);
    };

    // Events
    const onChangeStageNameTextFieldValue: InputProps["onChange"] = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: InputOnChangeData
    ) => {
        newValue ? setStageName(newValue.value) : setStageName('');
    };

    const onChangeOrderTextFieldValue: InputProps["onChange"] = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: InputOnChangeData
    ) => {
        newValue ? setStageOrder(parseInt(newValue.value)) : setStageOrder(0);
    };

    const onChangeCommentsTextFieldValue: TextareaProps["onChange"] = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: TextareaOnChangeData
    ) => {
        newValue ? setComments(newValue.value) : setComments('');
    };

    const onChangeIsOptionalToggleValue: SwitchProps["onChange"] = (ev: ChangeEvent<HTMLInputElement>, checked: SwitchOnChangeData) => {
        checked.checked ? setIsOptional(true) : setIsOptional(false);
    };

    const onChangeIsOnSiteMandatoryToggleValue: SwitchProps["onChange"] = (
        ev: ChangeEvent<HTMLInputElement>,
        checked: SwitchOnChangeData
    ) => {
        checked.checked ? setIsOnSiteMandatory(true) : setIsOnSiteMandatory(false);
    };

    const onChangeCycleIdTextFieldValue = (cycles: StageCycle[]) => {
        cycles.length > 0 ? setCycleId(cycles[0]?.id) : setCycleId(undefined);
    };

    const onChangeFailSelectionEnabledToggleValue: SwitchProps["onChange"] = (
        ev: ChangeEvent<HTMLInputElement>,
        checked: SwitchOnChangeData
    ) => {
        checked.checked ? setFailSelectionEnabled(true) : setFailSelectionEnabled(false);
    }

    const onChangeMarkAsPayableToggleValue: SwitchProps["onChange"] = (
        ev: ChangeEvent<HTMLInputElement>,
        checked: SwitchOnChangeData
    ) => {
        checked.checked ? setMarkAsPayable(true) : setMarkAsPayable(false);
    };

    const HandleSaveForm = () => {
        setSubmittedForm(true);
        if (ValidateForm()) {
            let CurrentValues: ContractStage | NewContractStage;
            if (props.Stage) {
                CurrentValues = {
                    id: props.Stage.id,
                    contractId: props.Stage.contractId,
                    contractAddendumId: props.ContractAddendumId,
                    stageName: stageName!,
                    comments: comments ?? '',
                    branchId: branchId,
                    isOptional: isOptional,
                    isOnSiteMandatory: isOnSiteMandatory,
                    failSelectionEnabled: failSelectionEnabled,
                    cycleId: cycleId!,
                    stageOrder: stageOrder!,
                    emrProtocolVisitIdRelated: emrProtocolVisitIdRelated,
                    internalCrossVersionId: props.Stage.internalCrossVersionId,
                    markAsSettleable: markAsSettleable,
                    markAsPayable: markAsPayable,
                    contractStageTypeId: contractStageTypeId,
                };
                props.UpdateContractStage(CurrentValues as ContractStage);

            } else {
                stageCountAtSubmit.current = props.TotalStages;
                CurrentValues = {
                    contractId: props.ContractId,
                    contractAddendumId: props.ContractAddendumId,
                    stageName: stageName!,
                    comments: comments ?? '',
                    branchId: branchId,
                    isOptional: isOptional,
                    isOnSiteMandatory: isOnSiteMandatory,
                    failSelectionEnabled: failSelectionEnabled,
                    cycleId: cycleId!,
                    stageOrder: stageOrder!,
                    emrProtocolVisitIdRelated: emrProtocolVisitIdRelated,
                    markAsSettleable: markAsSettleable,
                    markAsPayable: markAsPayable,
                    contractStageTypeId: contractStageTypeId,
                };
                props.AddContractStage(CurrentValues);
            }
        }
    };

    const HandleCancelForm = () => {
        ResetForm();
        props.dismissPanel();
    };

    const formFields = (
        <>
            <div className={styles.group}>
                <GuideField
                    required
                    label={t('stageForm.props.stageName')}
                    error={submittedForm && !stageName ? t('stageForm.props.requiredName') : undefined}
                >
                    <Input value={stageName ?? ''} onChange={onChangeStageNameTextFieldValue} />
                </GuideField>
                <CycleSelectorComponent
                    ContractId={props.ContractId}
                    ContractAddendumId={props.ContractAddendumId}
                    onSelectedCycles={onChangeCycleIdTextFieldValue}
                    CyclesSelected={props.Stage?.cycleId ? [props.Stage.cycleId] : []}
                    MultiselectEnable={false}
                />
                <ContractBranchSelectorComponent
                    ContractId={props.ContractId}
                    AddendumId={props.ContractAddendumId}
                    onSelectedContractBranch={(branches: ContractBranch[]) => setBranchId(branches[0]?.id)}
                    ContractBranchesSelected={branchId ? [branchId] : []}
                    MultiselectEnable={false}
                />
                <GuideField
                    required
                    label={t('stageForm.props.order')}
                    error={submittedForm && !stageOrder ? t('stageForm.props.requiredOrder') : undefined}
                >
                    <Input
                        type="number"
                        value={stageOrder?.toString() ?? ''}
                        onChange={onChangeOrderTextFieldValue}
                    />
                </GuideField>
            </div>

            <div className={styles.typeRow}>
                <div className={styles.typeSelector}>
                    <ContractStageTypesSelectorComponent
                        onSelectedType={(types: ContractStageType[]) => { setContractStageTypeId(types[0]?.id) }}
                        ContractStageTypeSelected={contractStageTypeId ? [contractStageTypeId] : []}
                        MultiselectEnable={false}
                    />
                </div>
                <Button
                    className={styles.typeSettings}
                    icon={<Settings24Regular />}
                    onClick={() => setIsStageTypeDrawerOpen(true)}
                    data-testid="stage-form-type-settings"
                />
                <StageTypeListComponent
                    isOpen={isStageTypeDrawerOpen}
                    dismissPanel={() => setIsStageTypeDrawerOpen(false)}
                    openPanel={function (): void {
                        throw new Error('Function not implemented.');
                    }}
                />
            </div>

            <div className={styles.switches}>
                <Field
                    className='ToggleInline'
                    validationState="none"
                    validationMessageIcon={<InfoFilled />}
                    validationMessage="Indica si la visita es opcional u obligatoria"
                >
                    <Switch
                        label={t('stageForm.props.optional')}
                        labelPosition="before"
                        checked={isOptional}
                        onChange={onChangeIsOptionalToggleValue}
                    />
                </Field>
                <Field
                    className='ToggleInline'
                    validationState="none"
                    validationMessageIcon={<InfoFilled />}
                    validationMessage="Indica si la visita se realiza de forma presencial o remota"
                >
                    <Switch
                        label={t('stageForm.props.inPerson')}
                        labelPosition="before"
                        checked={isOnSiteMandatory}
                        onChange={onChangeIsOnSiteMandatoryToggleValue}
                    />
                </Field>
                <Field
                    className='ToggleInline'
                    validationState="none"
                    validationMessageIcon={<InfoFilled />}
                    validationMessage="Indica si en esta visita es posible que ocurra un fallo de selección"
                >
                    <Switch
                        label={t('stageForm.props.enableSelectionFailure')}
                        labelPosition="before"
                        checked={failSelectionEnabled}
                        onChange={onChangeFailSelectionEnabledToggleValue}
                    />
                </Field>
                <Field
                    className='ToggleInline'
                    validationState="none"
                    validationMessageIcon={<InfoFilled />}
                    validationMessage="Indica si esta visita es tenida en cuenta para el pago al equipo de investigación"
                >
                    <Switch
                        label={t('stageForm.props.isPayable')}
                        labelPosition="before"
                        checked={markAsPayable}
                        onChange={onChangeMarkAsPayableToggleValue}
                    />
                </Field>
            </div>

            {props.ProtocolId && (
                <>
                    <Divider />
                    <ProtocolVisitSelectorComponent
                        ProtocolId={props.ProtocolId}
                        onSelectedVisit={(visit: ProtocolVisit[] | ProtocolVisit | undefined) => setEmrProtocolVisitIdRelated((visit as ProtocolVisit | undefined)?.crossVersionId)}
                        SelectedVisitIds={emrProtocolVisitIdRelated ? [emrProtocolVisitIdRelated] : []}
                        MultiselectEnable={false}
                    />
                </>
            )}

            <Divider />
            <GuideField label={t('stageForm.props.comments')}>
                <Textarea value={comments ?? ''} onChange={onChangeCommentsTextFieldValue} resize="vertical" />
            </GuideField>
        </>
    );

    // form validation

    const ValidateForm = (): boolean => {
        let validationResult: boolean = true;

        let FormValidation: FormValidation = { success: true, errors: [] };
        setFormValidationStatus(FormValidation);

        // validate Visit name
        if (!stageName) {
            FormValidation.errors.push(t('stageForm.props.requiredName'));
            validationResult = false;
        }

        // validate Order
        if (!stageOrder) {
            FormValidation.errors.push(t('stageForm.props.requiredOrder'));
            validationResult = false;
        }

        FormValidation.success = validationResult;
        setFormValidationStatus(FormValidation);

        return validationResult;
    };

    return (
        <GuideDrawer
            open={props.isOpen}
            size="medium"
            eyebrow={props.Contract?.contractNumber || undefined}
            title={t('stageForm.panel.visitInformation')}
            onDismiss={HandleCancelForm}
            preventDismiss={!!props.isSaving}
            secondaryAction={{
                label: t('stageForm.footer.cancelButton'),
                onClick: HandleCancelForm,
                disabled: props.isSaving
            }}
            primaryAction={{
                label: t('stageForm.footer.saveButton'),
                onClick: HandleSaveForm,
                loading: props.isSaving
            }}
            testId="stage-form-drawer"
        >
            {submittedForm && !FormValidationStatus?.success && (
                <GuideMessageBar intent="error">
                    <MessageBarBody>
                        <MessageBarTitle>{t('stageForm.panel.reviewInformation')}</MessageBarTitle>
                        <ul className={styles.errorList}>
                            {FormValidationStatus?.errors.map(
                                (Error: string, index: number) => (
                                    <li key={index}>{Error}</li>
                                )
                            )}
                        </ul>
                    </MessageBarBody>
                </GuideMessageBar>
            )}
            {props.failOnSaving &&
                props.error && (
                    <ErrorMessageComponent Error={props.error} />
                )}
            {formFields}
        </GuideDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ContractStageFormComponent as any);
